import { FocusTrap } from '@mui/base/FocusTrap';
import { ReportProblemTwoTone } from '@mui/icons-material';
import { Backdrop, Box, Button, SxProps, Typography } from '@mui/material';
import Image from 'components/Image/Image';
import { usePopups } from 'contexts/Popups/Popups';
import { clamp, parseHtml } from 'helpers';
import { createRef, RefObject, useCallback, useEffect, useState } from 'react';
import useResizeObserver from 'use-resize-observer';

export default function PopupDialog() {
    const [arrowStyles, setArrowStyles] = useState<SxProps | null>(null);
    const [backdropStyles, setBackdropStyles] = useState<SxProps | null>(null);
    const [contentStyles, setContentStyles] = useState<SxProps | null>(null);
    const [current, setCurrent] = useState<PopupProps | null>(null);
    const [highlightComponent, setHighlightComponent] = useState<HTMLElement | null>(null);
    const [observations, setObservations] = useState('');
    const [open, setOpen] = useState(false);
    const contentRef = createRef<HTMLDivElement>();
    const highlightCloneRef = createRef<HTMLDivElement>();
    const popups = usePopups();
    // React 19 widens ref objects to RefObject<T | null>; use-resize-observer's
    // types still expect the pre-19 non-null RefObject<T>, so cast at the boundary.
    const contentObserver = useResizeObserver<HTMLDivElement>({ ref: contentRef as RefObject<HTMLDivElement> });
    const highlightObserver = useResizeObserver<HTMLDivElement>({ ref: highlightCloneRef as RefObject<HTMLDivElement> });

    const handleClose = useCallback(
        (e?: any) => {
            const clickTarget = (e.type === 'click' && e?.target?.className) || '';
            const keyName = e.type === 'keydown' && e.key;
            if (
                popups.dialog &&
                (!e ||
                    clickTarget.includes('MuiButton-root') ||
                    clickTarget.includes('MuiBackdrop-root') ||
                    keyName === 'Enter' ||
                    keyName === 'Escape')
            ) {
                popups.hide(popups.dialog.name);
            }
        },
        [popups]
    );

    const handleResize = useCallback(() => {
        // Get clone and rects
        const clone = highlightCloneRef.current?.children[0] as HTMLElement | null;
        const contentRect = contentRef.current?.getBoundingClientRect();
        const highlightRect = highlightComponent?.getBoundingClientRect();
        const mainRect = document.getElementsByTagName('main')[0].getBoundingClientRect();

        if (clone && contentRect && highlightComponent && highlightRect && mainRect) {
            // Set clone styles
            clone.style.height = `${highlightRect.height}px`;
            clone.style.left = `${highlightRect.left}px`;
            clone.style.margin = '0px';
            clone.style.position = 'fixed';
            clone.style.top = `${highlightRect.top}px`;
            clone.style.width = `${highlightRect.width}px`;

            // Check highlight for canvas
            const highlightCanvas = highlightComponent.getElementsByTagName('canvas')[0];
            if (highlightCanvas) {
                // Resize clone canvas
                const cloneCanvas = (clone.parentElement as HTMLElement).getElementsByTagName('canvas')[0];
                cloneCanvas.height = highlightCanvas.height;
                cloneCanvas.width = highlightCanvas.width;

                // Copy content
                const cloneContext = cloneCanvas.getContext('2d');
                if (cloneContext) {
                    cloneContext.drawImage(highlightCanvas, 0, 0);
                }
            }

            // Get placement
            const placeRight = highlightRect.left < 500;

            // Get content styles
            const newContentStyles = {
                left: placeRight ? highlightRect.right : 'auto',
                marginX: 8,
                position: 'fixed',
                right: placeRight ? 'auto' : mainRect.width - highlightRect.left,
                top: clamp(
                    highlightRect.top + highlightRect.height / 2 - contentRect.height / 2,
                    mainRect.top + 16,
                    mainRect.top + mainRect.height - contentRect.height - 16
                ),
                width: placeRight ? '40vw' : 500,
            };
            setContentStyles(newContentStyles);

            // Get arrow styles
            const newArrowStyles = {
                left: placeRight ? highlightRect.right + 32 : highlightRect.left - 32,
                top: highlightRect.top + highlightRect.height / 2,
                transform: `scale(${placeRight ? -0.5 : 0.5}, 0.5) translate(-50%, -50%)`,
                transformOrigin: 'left top',
            };
            setArrowStyles(newArrowStyles);
        }
    }, [contentRef, highlightCloneRef, highlightComponent]);

    useEffect(() => {
        // Add listeners
        window.addEventListener('keydown', handleClose);
        window.addEventListener('load', handleResize);
        window.addEventListener('resize', handleResize);

        return () => {
            // Remove listeners
            window.removeEventListener('keydown', handleClose);
            window.removeEventListener('load', handleResize);
            window.removeEventListener('resize', handleResize);
        };
    }, [handleClose, handleResize]);

    useEffect(() => {
        if (!current && !open && popups.dialog) {
            // Check highlight map
            if (popups.dialog.highlight === 'map') {
                // Get backdrop styles
                const newBackdropStyles = {
                    padding: 5,
                    position: 'fixed',
                    width: 500,
                };
                setBackdropStyles(newBackdropStyles);

                // Get content styles
                const newContentStyles = {
                    position: 'fixed',
                    right: 'calc(50% - 100px)',
                    width: '40vw',
                };
                setContentStyles(newContentStyles);

                // Get arrow styles
                const newArrowStyles = {
                    right: 'calc(50% - 250px)',
                    top: '50%',
                    transform: 'scale(0.8) translate(-20%, -120%)',
                    transformOrigin: 'right bottom',
                };
                setArrowStyles(newArrowStyles);
            } else {
                // Get highlight component
                const newHighlightComponent =
                    (popups.dialog.highlight && document.getElementById(popups.dialog.highlight)) || null;
                setHighlightComponent(newHighlightComponent);
            }

            // Open popup
            setCurrent(popups.dialog);
            setOpen(true);
        } else if (open && (!popups.dialog || current?.name !== popups.dialog?.name)) {
            setOpen(false);
        }
    }, [current, open, popups.dialog]);

    useEffect(() => {
        if (highlightComponent && highlightCloneRef.current && !highlightCloneRef.current.hasChildNodes()) {
            // Append clone of highlight element
            const clone = highlightComponent.cloneNode(true);
            highlightCloneRef.current.appendChild(clone);
        }
    }, [highlightComponent, highlightCloneRef]);

    useEffect(() => {
        // Check observers states
        const newObservations = `${contentObserver.height}-${contentObserver.width}-${highlightObserver.height}-${highlightObserver.width}`;
        if (newObservations !== observations) {
            setObservations(newObservations);
            handleResize();
        }
    }, [
        observations,
        contentObserver.height,
        contentObserver.width,
        highlightObserver.height,
        highlightObserver.width,
        handleResize,
    ]);

    function handleClosed() {
        // Clear state
        setArrowStyles(null);
        setBackdropStyles(null);
        setContentStyles(null);
        setCurrent(null);
        setHighlightComponent(null);
        setObservations('');
    }

    return (
        <Backdrop
            aria-hidden={false}
            open={open}
            mountOnEnter
            sx={backdropStyles}
            unmountOnExit
            onClick={handleClose}
            onExited={handleClosed}
        >
            <Box ref={highlightCloneRef} sx={{ pointerEvents: 'none' }} />
            {arrowStyles && (
                <Box position="fixed" sx={arrowStyles} zIndex={1}>
                    <Image alt="" aria-hidden src="/assets/arrow-curved.svg" />
                </Box>
            )}
            <FocusTrap open={open}>
                <Box
                    aria-describedby="popup-dialog-text"
                    aria-labelledby="popup-dialog-title"
                    role="alertdialog"
                    alignItems="center"
                    bgcolor="white"
                    borderRadius={4}
                    display="flex"
                    maxWidth={600}
                    padding={4}
                    ref={contentRef}
                    sx={{ outline: 'none', ...contentStyles }}
                    tabIndex={-1}
                >
                    <Box
                        display="flex"
                        flex={7}
                        flexDirection="column"
                        justifyContent="space-between"
                        sx={{ userSelect: 'none' }}
                    >
                        <Box>
                            {(current?.icon || current?.title) && (
                                <Box display="flex" gap={1} marginBottom={3}>
                                    {current?.icon === 'danger' && (
                                        <ReportProblemTwoTone color="error" fontSize="large" />
                                    )}
                                    <Typography component="h2" id="popup-dialog-title" variant="roboto428">
                                        {current?.title}
                                    </Typography>
                                </Box>
                            )}
                            {current?.text && (
                                <Typography
                                    component="p"
                                    dangerouslySetInnerHTML={parseHtml(current.text)}
                                    id="popup-dialog-text"
                                    variant="roboto420"
                                />
                            )}
                        </Box>
                        <Box marginTop={3}>
                            <Button data-testid="popup-dialog-primary-action" onClick={handleClose}>{current?.button || 'OK'}</Button>
                        </Box>
                    </Box>
                    {current?.image && (
                        <Box flex={5} textAlign="right">
                            <Image
                                alt="Popup dialog graphic"
                                src={`/assets/${current?.image}`}
                                style={{ maxHeight: 200, maxWidth: 300 }}
                            />
                        </Box>
                    )}
                </Box>
            </FocusTrap>
        </Backdrop>
    );
}
